import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { chatTutorialVariantAtom, getCurrentTutorial, postponedSignalsAtom } from 'app/atoms';
import { useAtomValue } from 'jotai';
import type { ShowTutorialOverlayType } from 'app/types/tutorial';
import { Tutorial, TutorialRef } from 'app/types/tutorial';
import { Signal } from 'app/network/signals';
import { useMyActivePlans } from 'app/network/plan';
import { ListingType } from 'app/types/listing';
import { useOpenNewWithGuardForNavigation } from 'app/hooks/use-open-new';
import { useTrackNewListingSource } from 'app/network/listing';

import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getCurrentRouteBranchIdentity, navigationRef } from 'app/lib/root-navigation';
import { getTutorialStepCount } from 'app/lib/tutorial-steps';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { navigateToLegacyQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

export function useTutorialDefinitions(showTutorialOverlay: ShowTutorialOverlayType) {
  const { featureFlags } = useListingCatalog();
  const navigation = navigationRef as unknown as SpecificNavigationProp;
  const postponedSignals = useAtomValue(postponedSignalsAtom);
  const chatTutorialVariant = useAtomValue(chatTutorialVariantAtom);
  const userPlan = useMyActivePlans().data?.[0];
  const trackNewListingSource = useTrackNewListingSource();

  // This is used to display a modal with good-to-know text, and a Next button to continue the tutorial.
  // Or it will highlight an element in the UI, and ask the user to click it.
  // Or maybe both.
  const showTutorialModal = (
    step: number | 'Final',
    highlightElement?: TutorialRef,
    showNextCta?: boolean,
    isLastStep?: boolean,
    alternativeText?: 'Corp' | 'Plural' | 'Request',

    context?: Record<string, any>,
    padding?: { paddingHorizontal?: number; paddingVertical?: number },
  ) => {
    // Read the current tutorial imperatively: when a tutorial has just been
    // started, this callback can come from a stale closure where the
    // currentTutorialAtom value was still undefined (silently aborting step 1).
    const tutorial = (context?.currentTutorial as Tutorial | undefined) ?? getCurrentTutorial();
    if (!tutorial) return;
    const resolvedShowNextCta = showNextCta ?? !highlightElement;
    const stepWithAlt = alternativeText ? `${step.toString()}Alt${alternativeText}` : step;
    const ownerRouteKey =
      typeof context?.ownerRouteKey === 'string' ? context.ownerRouteKey : getCurrentRouteBranchIdentity();

    showTutorialOverlay(
      tutorial,
      undefined,
      `${tutorial}.step${stepWithAlt.toString()}.title`,
      `${tutorial}.step${stepWithAlt.toString()}.text`,
      resolvedShowNextCta,
      isLastStep,
      padding,
      highlightElement,
      typeof step === 'number' ? step : undefined,
      typeof step === 'number' ? getTutorialStepCount(tutorial) : undefined,
      ownerRouteKey,
    );
  };

  const openNewWithGuard = useOpenNewWithGuardForNavigation(navigation);
  const insets = useSafeAreaInsets();
  return {
    [Tutorial.Guardian]: [
      {
        delay: 300,
        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.GuardianLoanAction, true, true, undefined, context, {
            paddingHorizontal: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x1'],
          });
        },
      },
    ],
    [Tutorial.ContactMember]: [
      {
        delay: 300,

        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.ContactMemberButton, true, true, undefined, context, {
            paddingHorizontal: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x1'],
          });
        },
      },
    ],
    [Tutorial.ChatIntro]: [
      {
        delay: 0,

        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.ChatInputField, true, false, undefined, context, {
            paddingHorizontal: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x1'],
          });
        },
      },
      {
        delay: 100,

        then: (context?: Record<string, any>) => {
          const alt = chatTutorialVariant === 'request' ? 'Request' : undefined;
          showTutorialModal(2, TutorialRef.SuggestDatesButton, true, true, alt, context, {
            paddingHorizontal: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x2'],
          });
        },
      },
    ],
    [Tutorial.ChatCreation]: [
      {
        delay: 100,

        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.ChatCreationButton, true, true, undefined, context, {
            paddingHorizontal: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x1'],
          });
        },
      },
    ],
    [Tutorial.Conciergerie]: [
      {
        delay: 100,

        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.FirstConciergeChat, true, true, undefined, context, {
            paddingHorizontal: insets.left + insets.right || theme.spacing['spacing.general.x4'],
          });
        },
      },
    ],
    [Tutorial.HomeExplore]: [
      {
        delay: 300,

        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.HeaderSearchBar, true, false, undefined, context, {
            paddingHorizontal: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x1'],
          });
        },
      },
      {
        delay: 300,

        then: (context?: Record<string, any>) => {
          showTutorialModal(2, TutorialRef.PublishButton, true, false, undefined, context, {
            paddingHorizontal: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x1'],
          });
        },
      },
      {
        delay: 300,

        then: (context?: Record<string, any>) => {
          showTutorialModal(3, TutorialRef.HeaderHelpButton, true, true, undefined, context, {
            paddingHorizontal: theme.spacing['spacing.general.x1'],
            paddingVertical: theme.spacing['spacing.general.x1'],
          });
        },
      },
    ],
    [Tutorial.FirstConversation]: [
      {
        delay: 100,

        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.ChatInputField, true, true, undefined, context);
        },
      },
    ],
    [Tutorial.FirstMessageSent]: [
      {
        delay: 0,

        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.SuggestDatesButton, true, true, undefined, context);
        },
      },
    ],
    [Tutorial.MultipleOffers]: [
      {
        delay: 0,

        then: (context?: Record<string, any>) => {
          showTutorialModal(1, TutorialRef.PublishButton, false, false, undefined, context);
        },
      },
    ],
    [Tutorial.PublishOffers]: [
      {
        delay: 0,
        then: () => {
          // Should already be there, but eh
          navigation.navigate('billboard', {
            screen: 'all-listings',
            params: { type: ListingType.offer },
            initial: true,
          });
          setTimeout(() => showTutorialModal(1), 50);
        },
      },
      {
        delay: 100,

        then: (context?: Record<string, any>) => {
          const alt = userPlan?.code.group?.subtype ? 'Corp' : undefined;
          showTutorialModal(2, TutorialRef.PublishButton, undefined, false, alt, context);
        },
      },
      {
        delay: 0,
        then: () => {
          trackNewListingSource('tutorial');
          openNewWithGuard(navigation, { defaultValues: { type: ListingType.offer } });
          setTimeout(() => showTutorialModal(3), 350);
        },
      },
      {
        delay: 300,
        then: () => {
          trackNewListingSource('tutorial');
          navigateToLegacyQuickPublishFlow(navigation, { featureFlags });
        },
      },
      {
        delay: 0,
        then: (context?: { numListingsPublished?: number }) => {
          navigation.navigate('billboard', {
            screen: 'all-listings',
            params: { type: ListingType.offer },
            initial: true,
          });
          // @TODO How can we signal to all-listings that we want to be in the offers tab..?
          const alt = (context?.numListingsPublished ?? 0) > 1 ? 'Plural' : undefined;
          setTimeout(() => showTutorialModal(4, TutorialRef.FirstOfferInList, true, false, alt), 550);
        },
      },
      {
        delay: 0,
        then: () => {
          trackNewListingSource('tutorial');
          openNewWithGuard(navigation, { defaultValues: { type: ListingType.offer } });
          setTimeout(() => showTutorialModal(5, undefined, true, true), 350);
        },
      },
      {
        delay: 300,
        then: () => {
          navigation.goBack();
          setTimeout(() => {
            if (postponedSignals.some((s) => s === Signal.TriggeredFreeSub)) {
              navigation.navigate(
                'freeSub',
                {
                  finishedTutorial: true,
                  userNowContributor: postponedSignals.some((s) => s === Signal.UserLevelNowContributor),
                },
                { pop: true },
              );
            } else {
              // Let's assume with always have Signal.UserLevelNowContributor for now...
              showTutorialModal('Final');
            }
          }, 350);
        },
      },
    ],
  };
}
